import {
  ArrowLeft,
  LogOut,
  Trash2,
  Calendar as CalendarIcon,
  Plus,
  FlaskConical,
  Ghost,
} from 'lucide-react';
import Avatar from './Avatar';
import ProfileSettings from './ProfileSettings';

const MobileSettings = ({
  state,
  dispatch,
  handleSignOut,
  saveUserProfile,
  handleConnectGoogleCalendar,
  forceSyncAllToGoogle,
  handleFactoryReset,
  deferredPrompt,
  handleInstallClick,
  handleGoHome,
  handleGoTikiBar,
  isVault,
  handleRestoreChannel,
  handlePurgeChannel,
  handleImportProject,
  isImporting,
  user,
  username,
  userAvatar,
  bio,
  motivationalMessage,
  deletedChannels,
  profile,
  navigate,
}) => {
  const { nytmGoogleCalendarId, googleCalendars, enabledCalendars, appId } = state;

  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const createGhostUser = async () => {
    try {
      const { appId: currentAppId, version: currentVersion } = await import('../constants');
      const { db: currentDb } = await import('../firebase');
      const {
        doc: fireDoc,
        setDoc: fireSetDoc,
        serverTimestamp: fireTimestamp,
      } = await import('firebase/firestore');

      const ghostId = 'ghost-' + Math.floor(Math.random() * 10000);
      const ghostName = 'Ghost ' + ghostId.split('-')[1];
      const avatars = ['👻', '💀', '👽', '🤖', '🎃'];
      const ghostAvatar = avatars[Math.floor(Math.random() * avatars.length)];

      const ghostData = {
        id: ghostId,
        name: ghostName,
        avatar: ghostAvatar,
        bio: 'I am a test ghost user.',
        lastSeen: fireTimestamp(),
      };

      await fireSetDoc(
        fireDoc(currentDb, 'artifacts', currentAppId, 'public', 'data', 'users', ghostId),
        ghostData
      );

      // Store on window for Selenium to pick up without relying on alerts
      window.lastCreatedGhostName = ghostName;

      setTimeout(() => {
        alert(`Created ${ghostName}!`);
      }, 500);
    } catch (e) {
      console.error('GHOST CREATION ERROR:', e);
      alert('Failed to create ghost user: ' + e.message);
    }
  };

  const initializeGlobalChannel = async () => {
    if (import.meta.env.DEV) console.log('[DEV_TOOLS] initializeGlobalChannel called');
    try {
      const { appId: currentAppId, version: currentVersion } = await import('../constants');
      const { db: currentDb } = await import('../firebase');
      const {
        doc: fireDoc,
        setDoc: fireSetDoc,
        serverTimestamp: fireTimestamp,
      } = await import('firebase/firestore');

      if (import.meta.env.DEV) console.log('[DEV_TOOLS] Using appId:', currentAppId);
      const channelRef = fireDoc(
        currentDb,
        'artifacts',
        currentAppId,
        'public',
        'data',
        'channels',
        'general_chat_global'
      );
      await fireSetDoc(channelRef, {
        name: 'general',
        order: 0,
        createdAt: fireTimestamp(),
        createdBy: 'system',
      });
      if (import.meta.env.DEV) console.log('[DEV_TOOLS] Global channel written to Firestore');
      alert('Global channel initialized!');
    } catch (e) {
      console.error('[DEV_TOOLS] CHANNEL INIT ERROR:', e);
      alert('Failed to initialize channel: ' + e.message);
    }
  };

  const pushUpdateToFirestore = async () => {
    const isWorkingProvider = window.location.hostname.includes('--working-');
    const settingsDoc = isWorkingProvider ? 'settings_working' : 'settings';

    try {
      const { appId: currentAppId, version: currentVersion } = await import('../constants');
      const { db: currentDb } = await import('../firebase');
      const { doc: fireDoc, setDoc: fireSetDoc } = await import('firebase/firestore');

      await fireSetDoc(
        fireDoc(currentDb, 'artifacts', currentAppId, 'public', settingsDoc),
        {
          version: currentVersion,
        },
        { merge: true }
      );
      alert(`Version ${currentVersion} pushed to Firestore (${settingsDoc})!`);
    } catch (e) {
      console.error('PUSH UPDATE ERROR:', e);
      alert('Failed to push update: ' + e.message);
    }
  };

  return (
    <div className="flex flex-col bg-white h-full overflow-y-auto">
      <header className="pt-12 pb-4 px-6 flex items-center gap-4 border-b border-zinc-100 sticky top-0 bg-white z-20">
        <button onClick={() => navigate('/home')} className="p-1 -ml-1 text-zinc-500">
          <ArrowLeft size={24} />
        </button>
        <h1
          data-testid="settings-title"
          className="font-black text-zinc-900 text-xl tracking-tight uppercase"
        >
          Settings
        </h1>
      </header>

      <main className="flex-1 p-6 space-y-10 pb-32">
        {/* Install Section */}
        {!isStandalone && (
          <section className="flex flex-col items-start gap-4">
            <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
              Application
            </h2>
            <div className="w-full bg-zinc-900 text-white p-6 rounded-3xl shadow-xl shadow-zinc-200 relative overflow-hidden">
              <div className="relative z-10">
                <h3 className="text-xl font-black mb-2 tracking-tight">Install NYTM</h3>
                <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
                  Add this app to your home screen for a full-screen experience and quick access.
                </p>

                {deferredPrompt ? (
                  <button
                    onClick={handleInstallClick}
                    className="w-full py-4 bg-white text-zinc-900 font-black rounded-2xl shadow-lg active:scale-95 transition-all uppercase tracking-widest text-sm"
                  >
                    Install Now
                  </button>
                ) : isIOS ? (
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-[10px] font-black flex-shrink-0">
                        1
                      </div>
                      <p className="text-xs text-zinc-300">
                        Tap the <span className="font-bold text-white underline italic">Share</span>{' '}
                        button at the bottom of Safari.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-[10px] font-black flex-shrink-0">
                        2
                      </div>
                      <p className="text-xs text-zinc-300">
                        Scroll down and select{' '}
                        <span className="font-bold text-white underline">
                          &quot;Add to Home Screen&quot;
                        </span>
                        .
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest italic text-center">
                    Chrome is preparing the installer...
                  </p>
                )}
              </div>
              <div className="absolute -right-4 -bottom-4 opacity-10">
                <Plus size={120} strokeWidth={3} />
              </div>
            </div>
          </section>
        )}

        {/* Account Section */}
        <section className="flex flex-col items-start gap-4">
          <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
            Account
          </h2>
          <div className="w-full flex items-center gap-3 bg-zinc-50 p-4 rounded-2xl border border-zinc-100">
            <Avatar src={userAvatar} size={48} className="rounded-2xl" />
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-bold text-zinc-800 truncate">{user?.email}</span>
              <span className="text-[10px] text-green-500 font-bold uppercase tracking-tight">
                Active Session
              </span>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="w-full py-3 bg-red-50 text-red-600 text-xs font-bold rounded-xl hover:bg-red-100 transition-colors border border-red-100 uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <LogOut size={16} /> Sign Out
          </button>
        </section>

        {/* Profile Customization */}
        <div className="border-t border-zinc-100 pt-10">
          <ProfileSettings
            initialName={username}
            initialBio={bio}
            initialMotivation={motivationalMessage}
            initialAvatar={userAvatar}
            initialCities={profile?.cities || []}
            saveUserProfile={saveUserProfile}
            handleSignOut={handleSignOut}
            user={user}
            deletedChannels={deletedChannels}
            handleRestoreChannel={handleRestoreChannel}
            handlePurgeChannel={handlePurgeChannel}
            handleImportProject={handleImportProject}
            isImporting={isImporting}
            profile={profile}
          />
        </div>

        {/* Integrations */}
        <section className="flex flex-col items-start gap-4 border-t border-zinc-100 pt-10">
          <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
            Integrations
          </h2>

          <div
            className={`w-full flex flex-col p-4 rounded-2xl border ${sessionStorage.getItem('google_access_token') ? 'bg-green-50/30 border-green-100' : 'bg-zinc-50 border-zinc-100'}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center ${sessionStorage.getItem('google_access_token') ? 'bg-green-500 text-white shadow-lg shadow-green-200' : 'bg-zinc-200 text-zinc-400'}`}
                >
                  <CalendarIcon size={20} />
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-bold text-zinc-800">Google Calendar</p>
                  <p className="text-[10px] text-zinc-500">
                    {sessionStorage.getItem('google_access_token')
                      ? 'Connected (Full Access)'
                      : 'Not Connected'}
                  </p>
                </div>
              </div>
              {sessionStorage.getItem('google_access_token') ? (
                <button
                  onClick={() => {
                    sessionStorage.removeItem('google_access_token');
                    window.location.reload();
                  }}
                  className="text-[10px] font-black text-red-500 uppercase tracking-widest"
                >
                  Disconnect
                </button>
              ) : (
                <button
                  onClick={handleConnectGoogleCalendar}
                  className="px-4 py-2 bg-zinc-900 text-white text-[10px] font-black rounded-lg uppercase tracking-widest shadow-md"
                >
                  Connect
                </button>
              )}
            </div>

            {sessionStorage.getItem('google_access_token') && (
              <div className="space-y-4 pt-4 border-t border-zinc-100">
                <button
                  onClick={forceSyncAllToGoogle}
                  className="w-full py-2.5 bg-blue-50 text-blue-600 text-[10px] font-black rounded-lg uppercase tracking-widest border border-blue-100"
                >
                  Sync All Items Now
                </button>

                <div className="space-y-2">
                  <h3 className="text-[9px] font-black text-zinc-400 uppercase tracking-[0.2em] mb-2">
                    Display in Sidebar
                  </h3>
                  <div className="flex items-center justify-between p-3 bg-white/50 rounded-xl border border-zinc-100">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-xs font-bold text-zinc-700">NYTM Tasks</span>
                    </div>
                    <button
                      onClick={() =>
                        dispatch({ type: 'TOGGLE_CALENDAR_ENABLED', payload: 'nytm-tasks' })
                      }
                      className={`w-10 h-5 rounded-full transition-colors relative ${enabledCalendars.includes('nytm-tasks') ? 'bg-green-500' : 'bg-zinc-200'}`}
                    >
                      <div
                        className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${enabledCalendars.includes('nytm-tasks') ? 'left-6' : 'left-1'}`}
                      />
                    </button>
                  </div>
                  {googleCalendars.map((cal) => (
                    <div
                      key={cal.id}
                      className="flex items-center justify-between p-3 bg-white/50 rounded-xl border border-zinc-100"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: cal.backgroundColor }}
                        />
                        <span className="text-xs font-bold text-zinc-700 truncate max-w-[180px]">
                          {cal.summary}
                        </span>
                      </div>
                      <button
                        onClick={() =>
                          dispatch({ type: 'TOGGLE_CALENDAR_ENABLED', payload: cal.id })
                        }
                        className={`w-10 h-5 rounded-full transition-colors relative ${enabledCalendars.includes(cal.id) ? 'bg-zinc-900' : 'bg-zinc-200'}`}
                      >
                        <div
                          className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${enabledCalendars.includes(cal.id) ? 'left-6' : 'left-1'}`}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Admin Section */}
        {user?.email === 'brett.gaddy@gmail.com' && (
          <section className="flex flex-col items-start gap-4 border-t border-red-50 pt-10">
            <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-red-400">
              Danger Zone
            </h2>
            <button
              onClick={pushUpdateToFirestore}
              className="w-full py-3 bg-white text-blue-600 text-xs font-bold rounded-xl border-2 border-blue-100 uppercase tracking-widest flex items-center justify-center gap-2 shadow-sm mb-4"
            >
              <FlaskConical size={16} /> Push Update
            </button>
            <button
              onClick={handleFactoryReset}
              className="w-full py-3 bg-white text-red-600 text-xs font-bold rounded-xl border-2 border-red-100 uppercase tracking-widest flex items-center justify-center gap-2 shadow-sm"
            >
              <Trash2 size={16} /> Factory Reset
            </button>
          </section>
        )}

        {/* Development Tools */}
        {isVault && (
          <section className="flex flex-col items-start gap-4 border-t border-blue-50 pt-10">
            <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-400 flex items-center gap-2">
              <FlaskConical size={12} /> Development Tools
            </h2>
            <button
              id="init-global-channel-btn"
              data-testid="init-global-channel-btn"
              onClick={initializeGlobalChannel}
              className="w-full py-3 bg-blue-50 text-blue-600 text-xs font-bold rounded-xl border border-blue-100 uppercase tracking-widest flex items-center justify-center gap-2 shadow-sm active:scale-95 transition-all mb-4"
            >
              <FlaskConical size={16} /> Initialize Global Channel
            </button>
            <button
              id="create-ghost-user-btn"
              data-testid="create-ghost-user-btn"
              onClick={createGhostUser}
              className="w-full py-3 bg-blue-50 text-blue-600 text-xs font-bold rounded-xl border border-blue-100 uppercase tracking-widest flex items-center justify-center gap-2 shadow-sm active:scale-95 transition-all"
            >
              <Ghost size={16} /> Create Ghost User
            </button>
          </section>
        )}
      </main>
    </div>
  );
};

export default MobileSettings;
